import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { forwardRef, KeyboardEvent, SyntheticEvent, useRef, useState } from "react";
import { ControllerRenderProps, useFormContext } from "react-hook-form";

import { bssidRegex } from "./utils";

export interface Props extends ControllerRenderProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
}

export const BssidField = forwardRef(
  ({ label, placeholder, helperText = "", ...props }: Props, ref) => {
    const { formState, setError, clearErrors, setValue } = useFormContext();
    const autocompleteRef = useRef<HTMLInputElement>(null);

    const customErrorKey = props.name + "_persist";

    const [inputValue, setInputValue] = useState("");

    const clearError = () => {
      clearErrors(customErrorKey);
      clearErrors(props.name);
    };

    const validate = (e: KeyboardEvent) => {
      const values = props.value as string[];
      const typedValue = (e.target as HTMLInputElement).value.toLowerCase();

      if (!typedValue) {
        clearError();
        return;
      }

      const isInvalid = !bssidRegex.test(typedValue);
      const isDuplicate = values.map((bssid) => bssid.toLowerCase()).includes(typedValue);

      if (isInvalid) {
        setError(customErrorKey, {
          type: "custom",
          message: "BSSIDs must be in the format XX:XX:XX:XX:XX:XX using hexadecimal characters (0-9, A-F)"
        });
      } else if (isDuplicate) {
        setError(customErrorKey, {
          type: "custom",
          message: "Duplicate BSSIDs are not allowed"
        });
      } else {
        clearError();
      }
    };

    const customError = formState.errors[customErrorKey]?.message as string | undefined;
    // React Admin validators return translation keys (e.g., "@@react-admin@@ra.validation.required")
    // Since we only need a simple "Required" message, we check for the presence of an error
    // and provide our own message instead of dealing with translation keys
    const formError = formState.errors[props.name] ? "Required" : undefined;

    const error = customError || formError;

    const updateValueInForm = (event: SyntheticEvent, newValue: string[]) => {
      // Clear input when Autocomplete X is clicked (empty array)
      if (newValue.length === 0) {
        clearError();
        props.onChange(newValue);
        setInputValue(""); // Clear input
        return;
      }

      if (!customError) {
        props.onChange(newValue);
      } else {
        event.preventDefault();
      }
    };

    const handleChipClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();

      const text = (event.target as HTMLSpanElement).textContent;

      if (!text) {
        return;
      }
      const values = props.value as string[];

      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }

      setValue(
        props.name,
        values.filter((bssid) => bssid.toLowerCase() !== text.toLowerCase())
      );

      setInputValue(text);
    };

    const handleChipDelete = (chipToDelete: string) => () => {
      const values = props.value as string[];
      props.onChange(values.filter((bssid) => bssid.toLowerCase() !== chipToDelete.toLowerCase()));
      setInputValue(""); // Clear input on delete
    };

    return (
      <Autocomplete
        {...props}
        options={[]}
        multiple
        freeSolo
        autoSelect
        fullWidth
        onKeyUp={validate}
        onChange={updateValueInForm}
        renderTags={(value: string[], getTagProps) =>
          value.map((option: string, index: number) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              onClick={handleChipClick}
              onDelete={handleChipDelete(option)}
            />
          ))
        }
        sx={{
          "& .MuiFormControl-root": {
            mt: 0
          }
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                "data-testid": props.name
              }}
              label={label}
              placeholder={placeholder}
              helperText={
                helperText + (error ? `${helperText?.endsWith(".") ? " " : ". "}${error}.` : "")
              }
              inputRef={autocompleteRef}
              error={!!error}
              variant="outlined"
            />
          );
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => {
          setInputValue(value ?? "");
        }}
        ref={ref}
      />
    );
  }
);
